import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { DataGrid } from "../../components/dataGrid/dataGrid.tsx";
import { ParamType } from "../../types/grid.ts";
import {
    getTransactionStats,
    ignoreTransaction,
    stopCharging,
    TransactionStats,
} from "../../api/specialized.ts";
import { useToast } from "../../hooks/useToast.ts";
import { StatusIcon } from "../../components/ui/statusIcon.tsx";
import { GridEditModal } from "../../components/editSearch/gridEditModal.tsx";
import styles from "../../styles/formPage.module.scss";

interface TransactionDetailsPageProps {
    popupTransactionId?: number;
    onClose?: () => void;
}

export function TransactionDetailsPage({ popupTransactionId, onClose }: TransactionDetailsPageProps = {}) {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const toast = useToast();

    const transactionId = popupTransactionId ?? (parseInt(searchParams.get("transactionId") ?? "", 10) || 0);

    const [stats, setStats] = useState<TransactionStats | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!transactionId) return;
        const ac = new AbortController();
        getTransactionStats(transactionId, ac.signal).then(res => {
            if (!ac.signal.aborted && !res.bError && res.oData) setStats(res.oData);
        }).catch(() => {});
        return () => ac.abort();
    }, [transactionId]);

    const handleIgnore = async () => {
        if (!stats) return;
        const newIgnore = !stats.Ignore;
        setSaving(true);
        try {
            const res = await ignoreTransaction(stats.TransactionID, newIgnore);
            if (!res.bError && res.oData === true) {
                setStats({ ...stats, Ignore: newIgnore });
                toast.toast(newIgnore ? "Successfully ignored transaction" : "Successfully unignored transaction", "success");
            } else {
                toast.toast("Failed to update ignore status", "error");
            }
        } catch {
            toast.toast("Failed to update ignore status", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleStopFee = async (isInitial: boolean) => {
        if (!stats) return;
        const currentStop = isInitial ? stats.StopChargingInitialFee : stats.StopChargingLateFee;
        const newStop = !currentStop;
        const label = isInitial ? "initial fee" : "late fee";
        setSaving(true);
        try {
            const res = await stopCharging(stats.TransactionID, isInitial, newStop, newStop ? "No reason." : null);
            if (!res.bError && res.oData) {
                const data = res.oData;
                const updatedStats = { ...stats };
                if (isInitial) {
                    updatedStats.StopChargingInitialFee = data.StopChargingInitialFee as boolean;
                } else {
                    updatedStats.StopChargingLateFee = data.StopChargingLateFee as boolean;
                }
                setStats(updatedStats);
                const didChange = isInitial
                    ? updatedStats.StopChargingInitialFee !== currentStop
                    : updatedStats.StopChargingLateFee !== currentStop;
                if (didChange) {
                    toast.toast(`${newStop ? "Set" : "Removed"} flag to stop charging ${label} for transaction '${stats.TransactionID}'`, "success");
                } else {
                    toast.toast(`Failed to change flag to stop charging ${label} for transaction '${stats.TransactionID}'`, "error");
                }
            } else {
                toast.toast(`Failed to change flag to stop charging ${label}`, "error");
            }
        } catch {
            toast.toast(`Failed to change flag to stop charging ${label}`, "error");
        } finally {
            setSaving(false);
        }
    };

    const closeModal = onClose ?? (() => navigate(-1));

    const footerLeft = stats ? (
        <>
            <button
                className={stats.Ignore ? styles.primaryBtn : styles.dangerBtn}
                onClick={handleIgnore}
                disabled={saving}
                style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)" }}
            >
                <StatusIcon kind={stats.Ignore ? "ok" : "blocked"} style={{ color: "currentColor" }} />
                {stats.Ignore ? "Unignore" : "Ignore"}
            </button>
            <button
                className={stats.StopChargingInitialFee ? styles.primaryBtn : styles.dangerBtn}
                onClick={() => handleStopFee(true)}
                disabled={saving}
                style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)" }}
            >
                <StatusIcon kind={stats.StopChargingInitialFee ? "ok" : "stop"} style={{ color: "currentColor" }} />
                {stats.StopChargingInitialFee ? "Unstop Initial Fee" : "Stop Initial Fee"}
            </button>
            <button
                className={stats.StopChargingLateFee ? styles.primaryBtn : styles.dangerBtn}
                onClick={() => handleStopFee(false)}
                disabled={saving}
                style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)" }}
            >
                <StatusIcon kind={stats.StopChargingLateFee ? "ok" : "stop"} style={{ color: "currentColor" }} />
                {stats.StopChargingLateFee ? "Unstop Late Fee" : "Stop Late Fee"}
            </button>
        </>
    ) : null;

    return (
        <GridEditModal
            title={`Transaction Details${transactionId ? `: ${transactionId}` : ""}`}
            wide
            maxWidth="1400px"
            readOnly
            cancelLabel="Close"
            footerLeft={footerLeft}
            onClose={closeModal}
            onSave={() => { /* read-only */ }}
        >
            {transactionId ? (
                <>
                    <div style={{ marginBottom: "var(--space-4)" }}>
                        <h3 className={styles.sectionTitle}>Transaction Head</h3>
                        <DataGrid
                            key={`txHead-${transactionId}`}
                            name="transactionHeadSearch"
                            staticFilters={[{ sColumn: "iTransactionID", oValue: transactionId, Type: ParamType.Equal }]}
                            autoSearch
                            compact
                        />
                    </div>
                    <div style={{ marginBottom: "var(--space-4)" }}>
                        <h3 className={styles.sectionTitle}>Transaction Rows</h3>
                        <DataGrid
                            key={`txRows-${transactionId}`}
                            name="transactionRowSearch"
                            staticFilters={[{ sColumn: "iTransactionID", oValue: transactionId, Type: ParamType.Equal }]}
                            autoSearch
                            compact
                        />
                    </div>
                    <div style={{ marginBottom: "var(--space-4)" }}>
                        <h3 className={styles.sectionTitle}>Payment Log</h3>
                        <DataGrid
                            key={`payments-${transactionId}`}
                            name="transactionPaymentSearch"
                            staticFilters={[{ sColumn: "iTransactionID", oValue: transactionId, Type: ParamType.Specified }]}
                            autoSearch
                            compact
                        />
                    </div>
                </>
            ) : (
                <p>No transaction ID specified.</p>
            )}
        </GridEditModal>
    );
}
